import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { filter, find, isEmpty, isNil, map, noop, some, toString } from 'lodash'
import PropTypes from 'prop-types'

import { showErrorNotification } from '../../../core/helpers'
import { areSameValue, formatDateUk } from '../../../core/utils'

import { Select } from '../../../common/components/sections'
import { ALL_SELECT_FILTER_ITEM } from '../../../common/constants'

import {
  useDishesAvailableForQcCountsLazyQuery,
  useDishQaDatesLazyQuery,
} from '../../../dishes/hooks'

const MenuCurationTaskQaFilter = ({
  menuId,
  selectedQa,
  isDisabled,
  onChange,
  wasDoneInPreviousTwoWeeks,
}) => {
  const { data: usersData } = useSelector(store => store.common.users)

  const [
    fetchCounts,
    { data: availableForQcCounts, error: availableForQcCountsError },
  ] = useDishesAvailableForQcCountsLazyQuery({ wasDoneInPreviousTwoWeeks })

  const [fetchQaDates, { data: dishQaDates, error: dishQaDatesError }] =
    useDishQaDatesLazyQuery()

  useEffect(() => {
    if (availableForQcCountsError || dishQaDatesError) {
      showErrorNotification({
        title: 'Failed to get required filter data',
        description: availableForQcCountsError,
      })
    }
  }, [availableForQcCountsError, dishQaDatesError])

  useEffect(() => {
    if (!isDisabled) {
      fetchCounts(menuId)
    }
  }, [fetchCounts, isDisabled, menuId])

  useEffect(() => {
    if (isDisabled && !isNil(selectedQa)) {
      onChange(null)
    }
  }, [isDisabled, onChange, selectedQa])

  useEffect(() => {
    if (!isDisabled && !isEmpty(availableForQcCounts) && !isNil(menuId)) {
      const qaIds = map(availableForQcCounts, 'userId')
      fetchQaDates(menuId, qaIds)
    }
  }, [availableForQcCounts, fetchQaDates, isDisabled, menuId])

  const usersOptions = useMemo(
    () => [
      ALL_SELECT_FILTER_ITEM,
      ...map(
        filter(usersData, user =>
          some(availableForQcCounts, ({ userId }) =>
            areSameValue(userId, user.id),
          ),
        ),
        ({ id, name }) => {
          const dishCount = find(availableForQcCounts, ({ userId }) =>
            areSameValue(userId, id),
          )
          const qaDate = find(dishQaDates, ({ userId }) =>
            areSameValue(userId, id),
          )
          return {
            label: `${name} (${dishCount.count})${
              !isNil(qaDate) ? ` - ${formatDateUk(qaDate.qadAt)}` : ''
            }`,
            value: id,
          }
        },
      ),
    ],
    [availableForQcCounts, dishQaDates, usersData],
  )

  const selectedValue = useMemo(
    () => (!isNil(selectedQa) ? toString(selectedQa) : null),
    [selectedQa],
  )

  return (
    <Select
      allowClear
      showSearch
      options={usersOptions}
      value={selectedValue}
      onChange={onChange}
      disabled={isDisabled}
    />
  )
}

MenuCurationTaskQaFilter.propTypes = {
  menuId: PropTypes.number,
  selectedQa: PropTypes.number,
  onChange: PropTypes.func,
  isDisabled: PropTypes.bool,
  wasDoneInPreviousTwoWeeks: PropTypes.bool,
}

MenuCurationTaskQaFilter.defaultProps = {
  menuId: undefined,
  selectedQa: undefined,
  onChange: noop,
  isDisabled: false,
  wasDoneInPreviousTwoWeeks: false,
}

export default MenuCurationTaskQaFilter
